import React from 'react';
import { useTranslation } from 'next-i18next';
import clsx from 'clsx';
import Button from '@components/atoms/button/button';
import Icon from '@components/atoms/icon/icon';
import { isAuthenticationMandatory } from '@helpers/utils/products';
import { useContextSelector } from 'use-context-selector';
import { ModalContext } from '@context/modal.context';
import useProductShippingQuery from '@hooks/product/use-product-shipping-query';
import useProductQuery from '@hooks/product/use-product-query';
import dynamic from 'next/dynamic';
import styles from './additional-details.module.scss';

const ControlQualityModal = dynamic(
  () => import('@components/molecules/products/product-details/components/control-quality-modal/control-quality-modal'),
  {
    ssr: false,
  },
);

const DeliveryModal = dynamic(
  () => import('@components/molecules/products/product-details/components/delivery-modal/delivery-modal'),
  {
    ssr: false,
  },
);

const LocalizationModal = dynamic(
  () => import('@components/molecules/products/product-details/components/localisation-modal/localisation-modal'),
  {
    ssr: false,
  },
);

const AdditionalDetails: React.FC = (): React.JSX.Element => {
  const { t } = useTranslation();
  const { productShipping } = useProductShippingQuery();
  const { product } = useProductQuery();
  const openModal = useContextSelector(ModalContext, (v) => v.openModal);

  const deliveryData = {
    product,
    sendDay: productShipping?.sendDay,
    warehouse: productShipping?.warehouse.name,
    sendDelay: productShipping?.sendDelay,
    pays: productShipping?.destinationCountry.name,
  };

  const localisationData = {
    product,
    isLocal: product?.isLocal || false,
  };

  const openQualityControlModal = () =>
    openModal(<ControlQualityModal />, {
      styles: {
        content: {
          maxWidth: '580px',
          width: '100%',
        },
      },
    });

  const openLocalizationModal = () =>
    openModal(<LocalizationModal data={localisationData} />, {
      styles: {
        content: {
          maxWidth: '600px',
          width: '100%',
        },
      },
    });

  const openDeliveryModal = () =>
    openModal(<DeliveryModal data={deliveryData} />, {
      styles: {
        content: {
          maxWidth: '600px',
          width: '100%',
        },
      },
    });

  return (
    <div className={clsx(styles.additionalDetails, styles['additionalDetails--no-border'])}>
      <div className={styles.additionalDetails__item}>
        <Icon
          name="pin"
          className={styles.additionalDetails__icon}
        />
        <div className={styles.additionalDetails__text}>
          {productShipping && product.inStock ? (
            <span>
              {t('PRODUCT.DETAILS.PRODUCT_LOCALISATION_VC', {
                shipping: productShipping,
              })}
            </span>
          ) : (
            <span>
              {t('PRODUCT.DETAILS.PRODUCT_LOCALISATION', {
                product,
              })}
            </span>
          )}
          <Button
            disableDefaultStyling
            className={styles.additionalDetails__link}
            type="submit"
            onClick={openLocalizationModal}
            data-cy="open-localization-modal-button"
          >
            <u>{t('PRODUCT.DETAILS.MORE_INFO')}</u>
          </Button>
        </div>
      </div>
      {product.inStock && (
        <div className={styles.additionalDetails__item}>
          {productShipping && (
            <div>
              <Icon
                name="box-clock"
                className={clsx(styles.additionalDetails__icon, styles.additionalDetails__icon__delivery)}
              />
            </div>
          )}
          {productShipping && (
            <div className={styles.additionalDetails__text}>
              {product.inStock && (
                <div>
                  <span>
                    {t('PRODUCT.DETAILS.DELIVERY_ESTIMATED', {
                      shipping: productShipping,
                    })}
                  </span>
                  <Button
                    data-cy="open-delivery-modal-button"
                    disableDefaultStyling
                    type="submit"
                    className={styles.additionalDetails__link}
                    onClick={openDeliveryModal}
                  >
                    <u>{t('PRODUCT.DETAILS.MORE_INFO')}</u>
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>
      )}
      <div className={styles.additionalDetails__item}>
        <Icon
          name="checkmark"
          className={clsx(styles.additionalDetails__icon, styles.additionalDetails__icon__check)}
        />
        <div className={styles.additionalDetails__text}>
          <span>
            {product.inStock
              ? t('PRODUCT.DETAILS.QUALITY_CONTROL_ALREADY_DONE')
              : isAuthenticationMandatory(product)
              ? t('PRODUCT.DETAILS.QUALITY_CONTROL')
              : t('PRODUCT.DETAILS.OPTIONAL_QUALITY_CONTROL')}
          </span>
          <Button
            disableDefaultStyling
            className={styles.additionalDetails__link}
            type="submit"
            onClick={openQualityControlModal}
            data-cy="open-quality-control-modal-button"
          >
            <u>{t('PRODUCT.DETAILS.MORE_INFO')}</u>
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AdditionalDetails;
